import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Language } from '@/admin/languages';
import { LANGUAGE_OPTIONS } from '@/admin/languages';
import { HelpPlacementsForm } from '@/admin/sitecontent/HelpPlacementsForm';
import { parseLanguage } from '@/admin/sitecontent/listSearch';
import { HeaderRow, StatusMessage } from '@/admin/sitecontent/StatusMessage';
import { DetailPage } from '@/admin/ui/Editor';
import { Select } from '@/admin/ui/Form';
import { BackLink } from '@/admin/ui/NavLinks';
import { PageTitle } from '@/admin/ui/Page';
import api from '@/api';

export const Route = createFileRoute('/admin/help/placements')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): { language?: Language } => ({
    language: parseLanguage(s.language),
  }),
  component: HelpPlacements,
});

function HelpPlacements() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language: Language = Route.useSearch().language ?? 'EN';
  const settings = api.getAdminHelpPlacements.useQuery({ path: { language } });

  return (
    <DetailPage>
      <BackLink>
        <Link to="/admin/help/">{t('admin.help.backToArticles')}</Link>
      </BackLink>
      <HeaderRow>
        <PageTitle>{t('admin.help.placementsTitle')}</PageTitle>
        <Select
          aria-label={t('admin.common.language')}
          value={language}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            void navigate({
              to: './',
              search: {
                language: e.target.value as Language,
              },
            })
          }
        >
          {LANGUAGE_OPTIONS.map(([code, label]) => (
            <option key={code} value={code}>
              {t(label)}
            </option>
          ))}
        </Select>
      </HeaderRow>
      {settings.isLoading ? (
        <StatusMessage>{t('admin.help.loadingPlacements')}</StatusMessage>
      ) : settings.error || !settings.data ? (
        <StatusMessage $error>
          {t('admin.help.errorLoadingPlacements')}
        </StatusMessage>
      ) : (
        <HelpPlacementsForm
          // Remounts when the language changes or a save lands, so the form
          // re-baselines from what the server now holds.
          key={`${language}-${JSON.stringify(settings.data)}`}
          settings={settings.data}
        />
      )}
    </DetailPage>
  );
}
